export interface CookiePreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

export const CONSENT_KEY = 'cookie_consent';
export const CONSENT_EVENT = 'solsazon:consent-change';
export const COOKIE_SETTINGS_EVENT = 'solsazon:cookie-settings';
const MEASUREMENT_ID = 'G-105EW9SMMM';
let initialized = false;
let memoryPreferences: CookiePreferences | null = null;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
} & Record<string, unknown>;

export function getCookiePreferences(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  if (memoryPreferences) return memoryPreferences;
  try {
    const value = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    if (value && typeof value.analytics === 'boolean' && typeof value.marketing === 'boolean') {
      return { necessary: true, analytics: value.analytics, marketing: value.marketing };
    }
  } catch { /* Storage may be unavailable; retain this page's explicit choice. */ }
  return memoryPreferences;
}

export function isPrivateAnalyticsPath(pathname: string): boolean {
  return /^\/(admin|account|login|register|forgot-password|reset-password|order-success|order-confirmation|order-tracking|mis-puntos)(\/|$)/.test(pathname);
}

export function canCollectAnalytics(): boolean {
  return typeof window !== 'undefined'
    && getCookiePreferences()?.analytics === true
    && !isPrivateAnalyticsPath(window.location.pathname);
}

export function safeAnalyticsUrl(input: string): string {
  try {
    const url = new URL(input, window.location.origin);
    const safe = new URL(url.pathname, url.origin);
    const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_content', 'utm_term'];
    if (getCookiePreferences()?.marketing) campaignKeys.push('gclid', 'gbraid', 'wbraid', 'msclkid');
    if (!isPrivateAnalyticsPath(url.pathname)) {
      for (const key of campaignKeys) {
        const value = url.searchParams.get(key);
        if (value && value.length <= 200 && !value.includes('@')) safe.searchParams.set(key, value);
      }
    }
    return safe.href;
  } catch { return typeof window === 'undefined' ? '' : window.location.origin + '/'; }
}

export function analyticsPageContext() {
  const pageLocation = safeAnalyticsUrl(window.location.href);
  let pageReferrer = '';
  try { if (document.referrer) pageReferrer = new URL(document.referrer).origin + '/'; } catch { /* No referrer. */ }
  return { page_location: pageLocation, page_path: new URL(pageLocation).pathname, page_referrer: pageReferrer };
}

function consentState(preferences: CookiePreferences | null) {
  return {
    analytics_storage: preferences?.analytics ? 'granted' : 'denied',
    ad_storage: preferences?.marketing ? 'granted' : 'denied',
    ad_user_data: preferences?.marketing ? 'granted' : 'denied',
    ad_personalization: preferences?.marketing ? 'granted' : 'denied',
  };
}

export function syncAnalyticsPrivacy(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as AnalyticsWindow;
  const preferences = getCookiePreferences();
  w['ga-disable-' + MEASUREMENT_ID] = !canCollectAnalytics();
  if (!initialized) return;
  w.gtag?.('consent', 'update', consentState(preferences));
  if (!canCollectAnalytics()) return;
  // Explicit defaults also protect automatic events from inheriting private query strings.
  w.gtag?.('set', analyticsPageContext());
  if (!document.getElementById('solsazon-google-tag')) {
    w.gtag?.('js', new Date());
    w.gtag?.('config', MEASUREMENT_ID, {
      ...analyticsPageContext(), send_page_view: false,
      allow_google_signals: preferences?.marketing === true,
      allow_ad_personalization_signals: preferences?.marketing === true,
    });
    const script = document.createElement('script');
    script.id = 'solsazon-google-tag';
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + MEASUREMENT_ID;
    document.head.appendChild(script);
  }
}

export function initializeAnalytics(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as AnalyticsWindow;
  if (!initialized) {
    w.dataLayer = w.dataLayer || [];
    // Google's command queue uses the native Arguments object.
    // eslint-disable-next-line prefer-rest-params
    w.gtag = w.gtag || function () { w.dataLayer?.push(arguments); };
    w.gtag('consent', 'default', consentState(null));
    initialized = true;
  }
  syncAnalyticsPrivacy();
}

export function saveCookiePreferences(preferences: CookiePreferences): void {
  memoryPreferences = { necessary: true, analytics: preferences.analytics, marketing: preferences.marketing };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(memoryPreferences)); } catch { /* Page-local consent still applies. */ }
  initializeAnalytics();
  if (!preferences.analytics) {
    for (const cookie of document.cookie.split(';')) {
      const name = cookie.split('=')[0].trim();
      if (!/^(_ga(?:_|$)|_gid$|_gat(?:_|$))/.test(name)) continue;
      const domains = ['', window.location.hostname, window.location.hostname.replace(/^www\./, '')];
      for (const domain of domains) {
        document.cookie = `${name}=; Max-Age=0; path=/` + (domain ? `; domain=${domain}` : '');
      }
    }
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

export function openCookiePreferences(): void {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}
